import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { EventImplementationType } from './../enums/EventImplementationType';
import { formatDateIso } from '@/utilities/dateutils';

export class EventImplementation extends BaseObj {
    public ID: number;
    public __id: number;

    public BooleanValue: boolean | null;
    public __booleanvalue: boolean | null;

    public CategoryID: number;
    public __categoryid: number;

    public EventID: number;
    public __eventid: number;

    public RaceID: number;
    public __raceid: number;

    public StringValue: string | null;
    public __stringvalue: string | null;

    public TimeValue: Date | null;
    public __timevalue: Date | null;

    public TypeID: EventImplementationType;
    public __typeid: EventImplementationType;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        BooleanValue?: boolean | null;
        CategoryID?: number;
        EntityState?: EntityState;
        EventID?: number;
        RaceID?: number;
        StringValue?: string | null;
        TimeValue?: Date | null;
        TypeID?: EventImplementationType;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventImplementation>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventImplementation tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.BooleanValue = values.BooleanValue ?? null;
        this.__booleanvalue = values.BooleanValue ?? null;
        Object.defineProperty(this, 'BooleanValue', {
            get(): boolean | null {
                return this.__booleanvalue;
            },
            set(value: boolean | null) {
                this.__entityStateHandler.propertyChanged('BooleanValue', this.__booleanvalue, value);
                this.__booleanvalue = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryID = values.CategoryID ?? 0;
        this.__categoryid = values.CategoryID ?? 0;
        Object.defineProperty(this, 'CategoryID', {
            get(): number {
                return this.__categoryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('CategoryID', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StringValue = values.StringValue ?? null;
        this.__stringvalue = values.StringValue ?? null;
        Object.defineProperty(this, 'StringValue', {
            get(): string | null {
                return this.__stringvalue;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('StringValue', this.__stringvalue, value);
                this.__stringvalue = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TimeValue = values.TimeValue ?? null;
        this.__timevalue = values.TimeValue ?? null;
        Object.defineProperty(this, 'TimeValue', {
            get(): Date | null {
                return this.__timevalue;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('TimeValue', this.__timevalue, value);
                this.__timevalue = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TypeID = values.TypeID ?? 'AppealDeadline';
        this.__typeid = values.TypeID ?? 'AppealDeadline';
        Object.defineProperty(this, 'TypeID', {
            get(): EventImplementationType {
                return this.__typeid;
            },
            set(value: EventImplementationType) {
                this.__entityStateHandler.propertyChanged('TypeID', this.__typeid, value);
                this.__typeid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"BooleanValue": ${JSON.stringify(this.BooleanValue)}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"StringValue": ${JSON.stringify(this.StringValue)}` +
            ',' +
            `"TimeValue": ${this.TimeValue ? '"' + formatDateIso(this.TimeValue) + '"' : null}` +
            ',' +
            `"TypeID": ${this.TypeID == 'AppealDeadline' ? 1 : this.TypeID == 'OfficialLive' ? 2 : this.TypeID == 'OfficialPrinted' ? 3 : this.TypeID == 'UnofficialPrinted' ? 4 : 1}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventImplementation from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        BooleanValue?: boolean | null;
        CategoryID?: number;
        EventID?: number;
        RaceID?: number;
        StringValue?: string | null;
        TimeValue?: Date | null;
        TypeID?: EventImplementationType;
        EntityState?: 'Added' | 'Unchanged';
    }): EventImplementation {
        return new EventImplementation(values);
    }

    public static _FromServerJson(json: any): EventImplementation {
        return new EventImplementation({
            ID: json.ID,
            BooleanValue: json.BooleanValue ?? null,
            CategoryID: json.CategoryID,
            EventID: json.EventID,
            RaceID: json.RaceID,
            StringValue: json.StringValue ?? '',
            TimeValue: json.TimeValue ? (json.TimeValue instanceof Date ? json.TimeValue : new Date(json.TimeValue)) : null,
            TypeID:
                json.TypeID == 1
                    ? 'AppealDeadline'
                    : json.TypeID == 2
                      ? 'OfficialLive'
                      : json.TypeID == 3
                        ? 'OfficialPrinted'
                        : json.TypeID == 4
                          ? 'UnofficialPrinted'
                          : 'AppealDeadline',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventImplementation from another instance of EventImplementation.
     * @param other The EventImplementation you want to clone
     * @returns A cloned EventImplementation
     */
    public static FromOther(other: EventImplementation): EventImplementation {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventImplementation._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventImplementation.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: ('ID' | 'BooleanValue' | 'CategoryID' | 'EventID' | 'RaceID' | 'StringValue' | 'TimeValue' | 'TypeID')[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventImplementation';
}
